import { CurrencyUtils } from '@brunommsilva/react-foundations';
import { useToasts } from '@brunommsilva/react-foundations';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_IDS } from '../../../config/test-ids';
import useDeleteTransaction from '../../../hooks/transactions/use-delete-transaction';
import { Transaction } from '../../../models/transactions';

interface Props {
  show: boolean;
  transaction?: Transaction;
  onDeleted: () => void;
  onClose: () => void;
}

function DeleteTransactionModal({
  show,
  onDeleted,
  onClose,
  transaction,
}: Props) {
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const {
    deleteTransaction,
    isDeleting,
    wasDeletedWithSuccess,
    wasDeletedWithoutSuccess,
  } = useDeleteTransaction();

  const handleDeleteTransaction = () => {
    if (transaction) {
      deleteTransaction(transaction.id);
    }
  };
  const handleClose = () => onClose();

  useEffect(() => {
    if (wasDeletedWithSuccess) {
      showToast({
        title: t('DELETE_TRANSACTION_SUCCESS_TOAST.TITLE'),
        message: t('DELETE_TRANSACTION_SUCCESS_TOAST.MESSAGE'),
      });
      onDeleted();
    }
    if (wasDeletedWithoutSuccess) {
      showToast({
        title: t('DELETE_TRANSACTION_ERROR_TOAST.TITLE'),
        message: t('DELETE_TRANSACTION_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [wasDeletedWithSuccess, wasDeletedWithoutSuccess]);

  return (
    <>
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle>{t('DELETE_TRANSACTION_MODAL.TITLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {transaction &&
              t(`DELETE_TRANSACTION_MODAL.DESCRIPTION_${transaction.type}`, {
                description:
                  transaction.description || transaction.category.name,
                amount: CurrencyUtils.formatWithCurrency(transaction.amount),
              })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isDeleting} color="primary" onClick={handleClose}>
            {t('DELETE_TRANSACTION_MODAL.CLOSE')}
          </Button>
          <Button
            data-testid={TEST_IDS.transactions.deleteConfirm}
            disabled={isDeleting}
            color="error"
            onClick={handleDeleteTransaction}
          >
            {t('DELETE_TRANSACTION_MODAL.CONFIRM')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DeleteTransactionModal;
